import * as Sentry from "@sentry/browser";

import {
  API_URL_ADDRESS,
  API_URL_ADDRESS_LOOKUP,
  API_URL_CART,
  API_URL_CHECKOUT_SESSION,
  API_URL_CURRENT_ORDERS,
  API_URL_CUSTOMERINFO,
  API_URL_OPENING,
  API_URL_ORDER,
  API_URL_PAST_ORDERS,
  API_URL_PRODUCT,
} from "./config.js";
import { getJSON, putJSON, sendJSON } from "./helpers.js";
import { i18nPromise } from "./i18n";

// Create state object
export const state = {
  addressList: [],
  addressValidationReasons: [],
  cart: [],
  cartQuantity: undefined,
  checkoutSession: {},
  defaultUserInfo: {},
  loggedInUserInfo: {},
  openings: {},
  orderHistory: {},
  orderId: undefined,
  orderReceipt: {},
  orderState: {},
  product: {},
  productCart: {},
  productClone: {},
  searchItem: {
    // Querry is not used for now, it can help to set up analytics in the future to know for example which query are made the most
    query: "",
    results: [],
  },
  userTime: {},
};

// Maps order API errors to i18n translation keys for display to users.
export const handleOrderError = function (error) {
  const ERROR_TYPE_MAP = {
    FULFILLMENT_TIME_INVALID: "error.orderFulfillmentTimeInvalid",
    INVALID_CART_ITEM: "error.orderInvalidCartItem",
    MOBILE_MONEY_NOT_AVAILABLE: "error.mobileMoneyNotAvailable",
    ORDER_CREATION_FAILED: "error.orderCreationFailed",
    ORDER_DETAILS_NULL: "error.orderDetailsNull",
    PAYER_PHONE_INVALID: "error.payerPhoneInvalid",
    PAYMENT_METHOD_NOT_ALLOWED: "error.paymentMethodNotAllowed",
    PRODUCT_NOT_FOUND: "error.orderProductNotFound",
    PRODUCT_UNAVAILABLE: "error.orderProductUnavailable",
    // Handled by a dedicated redirect to the expiry page rather than an inline
    // message, but mapped so any other caller has something sensible to show.
    SESSION_EXPIRED: "error.paymentSessionExpired",
    STORE_OFFLINE: "error.orderStoreOffline",
    STORE_PAUSED: "error.orderStorePaused",
    UNKNOWN_ERROR: "error.orderUnknownError",
  };

  const DEFAULT_ERROR_KEY = "error.orderUnknownError";
  const serverMessage = error?.message || "Unknown error occurred";
  const errorType = error?.response?.data?.errorType || null;
  const userMessageKey = errorType
    ? ERROR_TYPE_MAP[errorType] || DEFAULT_ERROR_KEY
    : DEFAULT_ERROR_KEY;

  return { errorType, serverMessage, userMessageKey };
};

// Fetch product data from API
export const loadProduct = async function (id, serviceMode) {
  try {
    const data = await getJSON(`${API_URL_PRODUCT}${id}?type=${serviceMode}`);

    // Add parameter to the browser URL
    const urlParams = new URL(window.location.href);
    urlParams.searchParams.set("product-id", id);
    urlParams.searchParams.delete("openCart");
    urlParams.searchParams.delete("search");

    // Adds an entry to the browser's session history stack
    history.replaceState(data, "", urlParams);

    // Create product object
    const product = data.data;
    state.product = {
      description: product.description,
      idProduct: product.idProduct,
      imageUrl: product.imageUrl,
      modifierGroups: product.modifierGroups,
      name: product.name,
      price: product.price,
    };
  } catch (err) {
    // Temp error handling
    console.error(err);
    throw err;
  }
};

// Fetch address suggestions from the lookup API.
// The response shape is { addresses, validationReasons, suggestions }.
// addresses may be absent when no exact match is found, so both fields
// fall back to [] to avoid crashing callers that expect arrays.
export const loadAutocompleteAddress = async function (searchInput) {
  try {
    const data = await getJSON(
      `${API_URL_ADDRESS_LOOKUP}?query=${searchInput}`,
    );

    const responseData = data.data ?? {};
    state.addressList = Array.isArray(responseData.addresses)
      ? responseData.addresses
      : [];
    state.addressValidationReasons = Array.isArray(
      responseData.validationReasons,
    )
      ? responseData.validationReasons
      : [];
  } catch (err) {
    // Temp error handling
    console.error(err);
    throw err;
  }
};

// Maps API validationReasons to an i18n key for the customer-facing error message.
// Returns null if the address is valid (at least a commune or quartier was matched).
//
// An address is considered valid when ANY recognised geographic division is matched:
//   • commune:              COMMUNE_MATCH, COMMUNE_PERIPHERIQUE_MATCH
//   • neighbourhood/area:   QUARTIER_MATCH, SOUS_QUARTIER_MATCH, ZONE_REPERE_MATCH
//
// Previously both commune AND quartier were required, which rejected many real
// addresses that exist only at commune level in the database (no quartier row).
export const getAddressValidationKey = function (validationReasons) {
  // ADDRESS_NOT_FOUND means the API couldn't identify any geographic match at all.
  if (validationReasons.includes("ADDRESS_NOT_FOUND")) {
    return "address.missingCommuneAndQuartier";
  }

  const hasCommune =
    validationReasons.includes("COMMUNE_MATCH") ||
    validationReasons.includes("COMMUNE_PERIPHERIQUE_MATCH");

  const hasQuartier =
    validationReasons.includes("QUARTIER_MATCH") ||
    validationReasons.includes("SOUS_QUARTIER_MATCH") ||
    validationReasons.includes("ZONE_REPERE_MATCH");

  // Valid when at least one geographic division is matched.
  if (hasCommune || hasQuartier) {
    return null;
  }

  return "address.missingCommuneAndQuartier";
};

// Fetch product data from API
export const loadOrderReceipt = async function (id) {
  try {
    const data = await getJSON(`${API_URL_ORDER}/${id}`);

    // Add parameter to the browser URL
    const urlParams = new URL(window.location.href);
    urlParams.searchParams.set("orderReceipt", id);

    // Adds an entry to the browser's session history stack
    history.replaceState(data, "", urlParams);

    // Create order receipt object
    state.orderReceipt = data.data;
  } catch (err) {
    // Temp error handling
    console.error(err);
    throw err;
  }
};

export const updateCartCount = () => {
  const cartCountEl = document.querySelector(".cart-count");
  if (cartCountEl) {
    cartCountEl.textContent = state.cartQuantity.toString();
  }
};

// Remove product from cart API
export const removeCartProduct = async function (index, idStoreLocation) {
  try {
    await sendJSON(
      `${API_URL_CART}/${index}/delete?id_store_location=${idStoreLocation}`,
    );
  } catch (err) {
    // Temp error handling
    console.error(err);
    throw err;
  }
};

// Fetch store location data from API
export const loadStoreLocation = async function (idStoreLocation, serviceMode) {
  try {
    const data = await getJSON(
      `${API_URL_OPENING}${idStoreLocation}/openings?type=${serviceMode}`,
    );

    // Create openings object
    const openings = data.data;
    state.openings = {
      closingDays: openings.closingDays,
      deliveryTimes: openings.deliveryTimes,
      intervalDeliveryInMinutes: openings.intervalDeliveryInMinutes,
      timezone: openings.timezone,
    };
  } catch (err) {
    // Temp error handling
    console.error(err);
    throw err;
  }
};

// Add product to cart API
export const addToCart = async function (
  product,
  idStoreLocation,
  serviceMode,
) {
  try {
    await sendJSON(
      `${API_URL_CART}?id_store_location=${idStoreLocation}&type=${serviceMode}`,
      product,
    );
  } catch (err) {
    // Temp error handling
    console.error(err);
    throw err;
  }
};

// Place an order in the cart
export const createOrder = async function (
  payload,
  serviceMode,
  idStoreLocation,
) {
  try {
    const data = await sendJSON(
      `${API_URL_ORDER}?fulfillmentType=${serviceMode}&id_store_location=${idStoreLocation}`,
      payload,
    );

    // Get order ID from response
    const orderId = data.data?.id_order;
    if (!orderId) {
      throw new Error("Missing order ID in API response");
    }

    // Keep state update
    state.orderId = orderId;

    return orderId;
  } catch (err) {
    // Handle order-specific errors with structured error info
    const orderError = handleOrderError(err);

    // Attach structured error info to the error object
    err.orderError = orderError;

    // Log to console for debugging
    console.error("Order creation failed:", {
      errorType: orderError.errorType,
      serverMessage: orderError.serverMessage,
      userMessageKey: orderError.userMessageKey,
    });

    throw err;
  }
};

// Validate the checkout and open a temporary payment session for mobile-money orders.
// No restaurant order is created at this point.
export const createCheckoutSession = async function (
  payload,
  serviceMode,
  idStoreLocation,
) {
  try {
    const data = await sendJSON(
      `${API_URL_CHECKOUT_SESSION}?fulfillmentType=${serviceMode}&id_store_location=${idStoreLocation}`,
      payload,
    );

    // Get session identifiers from response
    const sessionToken = data.data?.sessionToken;
    if (!sessionToken) {
      throw new Error("Missing session token in API response");
    }

    // Keep state update
    state.checkoutSession = {
      sessionToken,
    };

    return sessionToken;
  } catch (err) {
    // Handle order-specific errors with structured error info
    const orderError = handleOrderError(err);

    // Attach structured error info to the error object
    err.orderError = orderError;

    // Log to console for debugging
    console.error("Checkout session creation failed:", {
      errorType: orderError.errorType,
      serverMessage: orderError.serverMessage,
      userMessageKey: orderError.userMessageKey,
    });

    throw err;
  }
};

// Confirm a mobile-money payment session and ask the backend to create the
// order. This function can't stop a second call for the same session from
// going out; not creating a duplicate order is the backend's job.
export const confirmMobileMoneyPayment = async function (
  sessionToken,
  wavePhoneNumber,
) {
  try {
    const data = await sendJSON(
      `${API_URL_CHECKOUT_SESSION}/${sessionToken}/confirm`,
      { wavePhoneNumber },
    );

    // Get order ID from response
    const orderId = data.data?.id_order;
    if (!orderId) {
      throw new Error("Missing order ID in API response");
    }

    // Keep state update
    state.orderId = orderId;

    return orderId;
  } catch (err) {
    // Handle order-specific errors with structured error info (includes SESSION_EXPIRED)
    const orderError = handleOrderError(err);

    // Attach structured error info to the error object
    err.orderError = orderError;

    // Log to console for debugging
    console.error("Mobile-money confirmation failed:", {
      errorType: orderError.errorType,
      serverMessage: orderError.serverMessage,
      userMessageKey: orderError.userMessageKey,
    });

    throw err;
  }
};

// Fetch cart data from API
export const loadCart = async function (serviceMode, idStoreLocation) {
  try {
    const data = await getJSON(
      `${API_URL_CART}?type=${serviceMode}&id_store_location=${idStoreLocation}`,
    );

    // Create cart object
    state.cart = data.data;

    // Create cartQuantity property
    state.cartQuantity = data.data.length;
  } catch (err) {
    // Temp error handling
    console.error(err);
    throw err;
  }
};

// Fetch order history data from API
export const loadOrderHistory = async function (id) {
  try {
    const data = await getJSON(`${API_URL_PAST_ORDERS}?id_store=${id}`);

    // Create order history object
    state.orderHistory = data.data;
  } catch (err) {
    // Temp error handling
    console.error(err);
    throw err;
  }
};

// Update product in the cart
export const updateProductCart = async function (product, idStoreLocation) {
  try {
    await putJSON(
      `${API_URL_CART}?id_store_location=${idStoreLocation}`,
      product,
    );
  } catch (err) {
    // Temp error handling
    console.error(err);
    throw err;
  }
};

// Fetch product data from cart
export const loadProductCart = function (index) {
  try {
    const { cart } = state;

    // Create product object
    const product = cart.items.find((el) => el.index === Number(index));

    if (!product) {
      throw new Error("Product not found in cart");
    }

    state.productCart = {
      description: product.description,
      idProduct: product.idProduct,
      imageUrl: product.imageUrl,
      index: product.index,
      ingredients: product.ingredients,
      instructions: product.instructions,
      modifierGroups: product.modifierGroups,
      name: product.name,
      price: product.price,
      priceTotal: product.priceTotal,
      quantity: product.quantity,
    };
  } catch (err) {
    // Capture and send the error to Sentry
    Sentry.captureException(err);

    // Temp error handling
    console.error(err);
    throw err;
  }
};

// Save user info in customer info API
export const saveUserInfo = async function (userInfo) {
  try {
    await sendJSON(`${API_URL_CUSTOMERINFO}`, userInfo);
  } catch (err) {
    // Temp error handling
    console.error(err);
    throw err;
  }
};

// Save user address in customer address API
export const saveUserAddress = async function (userAddress) {
  try {
    await sendJSON(`${API_URL_ADDRESS}`, userAddress);
  } catch (err) {
    // Temp error handling
    console.error(err);
    throw err;
  }
};

// Fetch the order state from API
export const loadOrderState = async function (idCustomerOrder) {
  try {
    const data = await getJSON(
      `${API_URL_CURRENT_ORDERS}${idCustomerOrder}/state`,
    );

    // Create order state object
    const { fulfillmentType, state: orderState } = data.data;
    state.orderState = orderState;
    state.fulfillmentType = fulfillmentType;
  } catch (err) {
    // Temp error handling
    console.error(err);
    throw err;
  }
};

// Fetch user info from API
export const loadUserInfo = async function () {
  try {
    const data = await getJSON(`${API_URL_CUSTOMERINFO}`);

    // Create user info object
    const loggedInUserInfo = data.data;
    state.loggedInUserInfo = {
      address: loggedInUserInfo.addressLine1,
      apartmentNumber: loggedInUserInfo.addressLine2,
      birthDate: loggedInUserInfo.birthDate,
      city: loggedInUserInfo.city,
      country: loggedInUserInfo.country,
      email: loggedInUserInfo.email,
      firstName: loggedInUserInfo.firstName,
      lastName: loggedInUserInfo.lastName,
      phoneNumber: loggedInUserInfo.phoneNumber,
    };
  } catch (err) {
    // Temp error handling
    console.error(err);
    throw err;
  }
};

export const loadSearchItem = async function (query, idStoreLocation) {
  try {
    state.searchItem.query = query;
    const data = await getJSON(
      `${API_URL_PRODUCT}search?q=${query}&id_store_location=${idStoreLocation}`,
    );

    // Add parameter to the browser URL
    const urlParams = new URL(window.location.href);
    urlParams.searchParams.set("search", query);
    urlParams.searchParams.delete("product-id");
    urlParams.searchParams.delete("openCart");

    // Adds an entry to the browser's session history stack
    history.replaceState(data, "", urlParams);

    // Create search object
    const product = data.data;
    state.searchItem.results = product.map((prod) => ({
      description: prod.description,
      idProduct: prod.idProduct,
      imageUrl: prod.imageUrl,
      name: prod.name,
      price: prod.price,
    }));
  } catch (err) {
    // Temp error handling
    console.error(err);
    throw err;
  }
};

export const getCheckedValue = function (product) {
  const MODIFIER_ITEM_NAME_INDEX = 2;
  const MODIFIER_ID_INDEX = 0;
  // Create ingredients array
  product.ingredients = [];

  const allInputs = document.querySelectorAll(".modifier-container input");

  for (let i = 0; i < allInputs.length; i++) {
    if (allInputs[i].checked) {
      // Get the modifier name
      const modifierName =
        allInputs[i].parentElement.children[MODIFIER_ITEM_NAME_INDEX]
          .textContent;

      // Get the modifier ID
      const modifierId = Number(
        allInputs[i].id.match(/(\d+)/u)[MODIFIER_ID_INDEX],
      );

      product.ingredients.push({
        idModifierItem: modifierId,
        name: modifierName,
      });
    }
  }
};

const compareArrays = (a, b) => {
  // Extract IDs from a and b
  a = a.map((obj) => obj.idModifierItem);
  b = b.map((obj) => obj.idModifierItem);

  // Check if the lengths are equal and if every element is equal at each index
  return (
    a.length === b.length && a.every((element, index) => element === b[index])
  );
};

const extractNumberFromCurrency = (currencyString) => {
  // Remove all non-numeric characters except the decimal point
  const numericValue = currencyString.replace(/[^0-9.]/gu, "");
  return parseFloat(numericValue);
};

export const updateProduct = function (prod) {
  const MODIFIER_ITEM_NAME_INDEX = 2;
  const MODIFIER_ID_INDEX = 0;

  // Get all input elements
  const allInputs = document.querySelectorAll(".modifier-container input");
  // Get user quantity
  const checkedQuantity = document.querySelector(".item-count").textContent;
  // Get total price
  const checkedpriceTotal = extractNumberFromCurrency(
    document.querySelector(".update-cart .item-price-modal").textContent,
  );
  // Get user instructions
  const checkedInstructions = document
    .querySelector(".special-instruction-inputfield")
    .value.trim();

  const checkedIngredients = [];

  // Get checked ingredients
  for (let i = 0; i < allInputs.length; i++) {
    if (allInputs[i].checked) {
      // Get modifier name
      const modifierName =
        allInputs[i].parentElement.children[MODIFIER_ITEM_NAME_INDEX]
          .textContent;

      // Get modifier ID
      const modifierId = Number(
        allInputs[i].id.match(/(\d+)/u)[MODIFIER_ID_INDEX],
      );

      checkedIngredients.push({
        idModifierItem: modifierId,
        name: modifierName,
      });
    }
  }

  // Update the quantity of the product if the user has modified it
  if (checkedQuantity !== prod.quantity) {
    prod.quantity = checkedQuantity;
  }

  // Update the instructions of the product if the user has modified it
  if (checkedInstructions !== prod.instructions) {
    prod.instructions = checkedInstructions;
  }

  // Update the total price of the product if the user has modified the quantity or the ingredients
  if (checkedpriceTotal !== prod.priceTotal) {
    prod.priceTotal = Number(checkedpriceTotal);
  }

  // Update the ingredients of the product if the user has modified them
  if (!compareArrays(prod.ingredients, checkedIngredients)) {
    prod.ingredients = checkedIngredients;
  }
};

export const fillInputs = function (productIngredient) {
  const MODIFIER_ID_INDEX = 0;
  let allInputs = document.querySelectorAll(".modifier-container input");
  // Convert NodeList to a shallow-copied Array
  allInputs = Array.from(allInputs);
  // Iterating through productIngredient
  for (const el of productIngredient) {
    // Check if allInputs contains the element of productIngredient
    for (const elAll of allInputs) {
      if (
        el.idModifierItem ===
        Number(elAll.id.match(/(\d+)/u)[MODIFIER_ID_INDEX])
      ) {
        elAll.checked = true;
      }
    }
  }
};

export const isObjectEmpty = (objectName) =>
  JSON.stringify(objectName) === "{}";

const persistUserInfo = function () {
  localStorage.setItem(
    "defaultUserInfo",
    JSON.stringify(state.defaultUserInfo),
  );
};

export const loadLoggedInUserFromDOM = function () {
  // Parse server-rendered user context from DOM
  const userContextElement = document.querySelector("#user-context");

  if (!userContextElement) {
    // No user context in DOM (guest user)
    return;
  }

  try {
    // Get the JSON data from the data attribute
    const userContextJson =
      userContextElement.getAttribute("data-user-context");

    if (!userContextJson) {
      return;
    }

    // Parse the JSON
    const userContextData = JSON.parse(userContextJson);

    // Map server fields to state fields and populate loggedInUserInfo
    state.loggedInUserInfo = {
      address: userContextData.addressLine1 || "",
      apartmentNumber: userContextData.addressLine2 || "",
      city: userContextData.city || "",
      country: userContextData.country || "",
      email: userContextData.email || "",
      firstName: userContextData.firstName || "",
      lastName: userContextData.lastName || "",
      phoneNumber: userContextData.phoneNumber || "",
    };
  } catch (err) {
    // Capture and send the error to Sentry
    Sentry.captureException(err);

    console.error("Failed to parse user context from DOM:", err);
  }
};

// Helper function to parse address and extract city
const parseAddressAndCity = function (addressString) {
  const NOT_FOUND_INDEX = -1;
  const START_INDEX = 0;
  const NEXT_CHAR_OFFSET = 1;

  const trimmed = addressString.trim();
  const lastCommaIndex = trimmed.lastIndexOf(",");

  if (lastCommaIndex === NOT_FOUND_INDEX) {
    // No comma found, treat entire string as street address
    return { city: "", streetAddress: trimmed };
  }

  const streetAddress = trimmed.slice(START_INDEX, lastCommaIndex).trim();
  const city = trimmed.slice(lastCommaIndex + NEXT_CHAR_OFFSET).trim();

  return { city, streetAddress };
};

export const updateUserInfo = function (isLoggedIn = false) {
  if (isLoggedIn) {
    // LOGGED-IN BRANCH: Update only editable address fields
    // Name/email/phone/country are readonly and already in loggedInUserInfo from DOM
    const { loggedInUserInfo } = state;

    const addressElement = document.querySelector("#address");
    const apartmentElement = document.querySelector("#apartment");

    // Update only the editable fields that the user can change
    if (addressElement && loggedInUserInfo.address !== addressElement.value) {
      const { city, streetAddress } = parseAddressAndCity(addressElement.value);
      loggedInUserInfo.address = streetAddress;
      loggedInUserInfo.city = city;
    }
    if (
      apartmentElement &&
      loggedInUserInfo.apartmentNumber !== apartmentElement.value
    ) {
      loggedInUserInfo.apartmentNumber = apartmentElement.value.trim();
    }
    // Do NOT persist to localStorage (logged-in users' data lives on server)
  } else {
    // GUEST BRANCH: Update all fields in defaultUserInfo + persist
    const { defaultUserInfo } = state;

    const addressElement = document.querySelector("#address");
    const apartmentElement = document.querySelector("#apartment");
    const phoneNumberElement = document.querySelector("#phone-number");
    const countryElement = document.querySelector("#country");
    const emailElement = document.querySelector("#email");
    const nameElement = document.querySelector("#name");

    if (addressElement && defaultUserInfo.address !== addressElement.value) {
      const { city, streetAddress } = parseAddressAndCity(addressElement.value);
      defaultUserInfo.address = streetAddress;
      defaultUserInfo.city = city;
    }
    if (
      apartmentElement &&
      defaultUserInfo.apartmentNumber !== apartmentElement.value
    ) {
      defaultUserInfo.apartmentNumber = apartmentElement.value.trim();
    }
    if (countryElement && defaultUserInfo.country !== countryElement.value) {
      defaultUserInfo.country = countryElement.value.trim();
    }
    if (
      phoneNumberElement &&
      defaultUserInfo.phoneNumber !== phoneNumberElement.value
    ) {
      defaultUserInfo.phoneNumber = phoneNumberElement.value.trim();
    }
    if (emailElement && defaultUserInfo.email !== emailElement.value) {
      defaultUserInfo.email = emailElement.value.trim();
    }
    if (nameElement && defaultUserInfo.name !== nameElement.value) {
      defaultUserInfo.name = nameElement.value.trim();
    }

    // Persist guest user info to localStorage
    persistUserInfo();
  }
};

export const updateDeliveryTime = function () {
  const { userTime } = state;

  // Update delivery time and day
  if (userTime.deliveryTime !== document.querySelector("#service-time").value) {
    userTime.deliveryTime = document.querySelector("#service-time").value;
  }
  if (userTime.deliveryDay !== document.querySelector("#service-date").value) {
    userTime.deliveryDay = document.querySelector("#service-date").value;
  }
};

const init = function () {
  // Define the default guest info shape
  const DEFAULT_GUEST_INFO = {
    address: "",
    apartmentNumber: "",
    city: "",
    country: "",
    email: "",
    name: "",
    phoneNumber: "",
  };

  const storage = localStorage.getItem("defaultUserInfo");

  if (storage) {
    try {
      const parsed = JSON.parse(storage);
      // Merge stored data with defaults to ensure all keys exist
      state.defaultUserInfo = { ...DEFAULT_GUEST_INFO, ...parsed };
    } catch {
      // If parsing fails, use defaults
      state.defaultUserInfo = DEFAULT_GUEST_INFO;
    }
  } else {
    // First visit: use defaults
    state.defaultUserInfo = DEFAULT_GUEST_INFO;
  }
};

// Create an async function to start the application
const startApp = async () => {
  try {
    // Wait for i18next to be fully initialized
    await i18nPromise;

    // Now that translations are ready, run the main app logic
    init();
  } catch (err) {
    // Capture and send the error to Sentry
    Sentry.captureException(err);

    console.error("Failed to initialize the application:", err);
  }
};

// Start the application
startApp();
